import { IVenue } from "app/app.types";
import AccountService from 'app/services/account/account.service';
import {appValues, Environment} from "appRoot/app.constants";
import DateUtilsService from "external-services/date-utils-service";
import AdminService from "external-services/diary-admin-service/admin.service";
import LocalStorageService from 'external-services/local-storage-service/localStorage.service';
import { LocalStorageKey } from "shared-types/SharedEnums";

const NS = 'TrackingService';


/**
 * This service was originally used with Countly for tracking custom events and page views, but
 * Countly account was removed. Left service in case we need to add a new tracking service at some point.
 * REF: https://nowbookit.atlassian.net/browse/NBI-725
 */

interface IUserInfo {
  key?: string;
  segmentation?: {
    page?: string;
    accountId?: string;
    venueId?: number;
    version?: string;
  }
}

interface IVenueExtend extends IVenue {
  currency: string;
  additionalBookingRequirements: {
      requiresVerification: boolean;
      verificationHeader: string;
      verificationMessage: string;
  },
}

interface IUser {
  id: string;
  accountId: string;
  firstName: string;
  lastName: string;
  surname: string;
  email: string;
  country: string;
  suburb: string;
  state: string;
  postcode: string;
  phone: string;
  pin: string;
  createdAt: string;
  roles: {
    name: string;
  }[],
  theme: string;
  accentColour: string;
  font: string;
  widgetVersion: number;
  venues: {
    id: number;
    name: string;
    active:  boolean;
    activeModifiedAt: string;
    country: string;
    state: string;
    subscriptionType: string;
    renewalDate: string;
    theme: string;
    accentColour: string;
    font: string;
    widgetVersion: number;
    isUsingAccountWidgetSettings: boolean;
    isPosActive: boolean;
  }[],
}


class TrackingService{
  private $rootScope: any;
  private appValues: any;

  private pendoInitialized = false;

  public setup($rootScope: any, appValues: any): void {
    this.$rootScope = $rootScope;
    this.appValues = appValues;
  }

  public init(): void {
    // track page tracking
    this.$rootScope.$on('$stateChangeSuccess', (evt: any, pageState: any) => {
      this.trackPageView(pageState.name);
    });
  }

  /**
   * Track a page view
   * @param {String} pageName     The name of the page
   */
  public trackPageView(pageName: string): void {
    const data: IUserInfo = {};
    data.key = "Page view";
    const segmentation = {
      page: pageName
    };
    data.segmentation = segmentation;
    this.addUserInfo(data);
    // @todo: add a tracking service for page views
  }

  /**
   * Track an event that occurs in the system
   * @param  {String} eventName   The name of the event
   * @param  {Object} values      An Object of key value pairs
   */
   public trackEvent(eventName: any, values: any = null): void {
    const data: any = {};
    data.key = eventName;
    if (values) {
      data.segmentation = values;
    } else {
      data.segmentation = {};
    }
    this.addUserInfo(data);
    // @todo: add a tracking service for custom events
  }

  /**
   * Add usrer detail to tracking so we know who is using the system
   */
   public trackUser(user: IUser, venue: IVenueExtend): void {
    // let user: any = {};
    // if (venue && account) {
    //   user = {
    //     "name": venue.name,
    //     "custom": {
    //       "venueId": venue.id,
    //       "accountId": account.id,
    //       "state": venue.state,
    //       "postcode": venue.postcode
    //     }
    //   };
    // }
    // @todo: add a tracking service for user data

    this.initPendoAnalytics(user, venue);
  }

  public trackError(error: any): void {
    // @todo: add a tracking service for errors
  }

  public storeOfflineStatus(reasons: string[]) {
    const time = Date.now();

    const existingOfflineStatus = LocalStorageService.get(LocalStorageKey.offlineStatus);
    if (existingOfflineStatus) {
      const {duration} = this.getDuration(existingOfflineStatus.time);

      // if (duration.seconds <= 10) { // use smaller values for testing locally
    if (duration.hours <= 2) {
        /**
         * Don't want to overwrite recent offline data (2 hours old).
         * If it is more than 2 hours old, it can start again, as most users would be back online within 
         * that time.
         * If they locked their device and came back later and are online, it's ok to track the previous
         * data, even though could be more than 2 hours old.
         */
        console.log(NS, 'offlineStatus data is too recent, ignoring', duration, existingOfflineStatus.reasons);
        return;
      }

    }

    LocalStorageService.set(LocalStorageKey.offlineStatus, {
      time,
      reasons,
    });
  }

  public trackOfflineStatus() {
    if (!window.pendo) {
      console.log(NS, 'Note: Pendo is not configured for this environment.');
      return;
    }

    // don't need pendo for local dev
    if (!window.config.pendo.enable) {
      console.log(NS, "pendo disabled for local environment.");
      return;
    }

    const offlineStatus = LocalStorageService.get(LocalStorageKey.offlineStatus);

    if (!offlineStatus?.time) return;

    const {duration, durationInMS} = this.getDuration(offlineStatus.time);
    if (duration.seconds < 5) {
      // if less than 5 seconds, just a blip, ignore it
      LocalStorageService.remove(LocalStorageKey.offlineStatus);
      return;
    }

    const trackingData = {
      durationInMS,
      duration,
      reasons: offlineStatus.reasons || [],
      accountId: window.pendo.getAccountId && window.pendo.getAccountId(),   // Get current Pendo accountId
      visitorId: window.pendo.getVisitorId && window.pendo.getVisitorId()    // Get current Pendo visitorId
    };

    console.log(NS, 'pendo - offlineStatus', trackingData);
    window.pendo.track('offlineStatus', trackingData);
    LocalStorageService.remove(LocalStorageKey.offlineStatus);
  }

  //--------Private function----------------

  private getDuration(offlineTime: number) {
    const durationInMS = Date.now() - offlineTime;
    const duration = DateUtilsService.getHoursMinsSecondsFromMS(durationInMS);
    return {duration, durationInMS};
  }

  private addUserInfo(data: IUserInfo): void {
    const account = AdminService.getAccount();
    const venue = AdminService.getVenue();
    console.log(account)
    console.log(venue)
    if (account && account.id) {
      data.segmentation.accountId = account.id;
    }
    if (venue && venue.id) {
      data.segmentation.venueId = venue.id;
    }
    data.segmentation.version = this.appValues.VERSION;
  }



  private initPendoAnalytics(user: IUser, venue: IVenueExtend): void {

    const versionNumber = this.appValues.VERSION;

    if (!window.pendo) {
      console.log(NS, 'Note: Pendo is not configured for this environment.')
      return;
    }

    /**
     * PENDO ANALYTICS: NBI-2186
     * Call this whenever information about your visitors becomes available.
     * Please use Strings, Numbers, or Bools for value types.
     */
    if (window.pendo && user && user.id && venue) {
      AccountService.getAccountById(venue.accountId)
        .then((response: any) => {

          const {businessName} = response;
          const { accountId, createdAt } = venue;

          /**
           * @todo: look at creating a submodule for pendo.initialize and use it in both diary and admin so their data
           * stays in sync.
           */

          const visitor = {
            id: user.id, // this is the id unique to each user, not the venue or account
            email: user.email,
            first_name: user.firstName,
            last_name: user.surname,
            roles: user.roles ? user.roles.map(({name}) => name).join(', ') : ''
          };

          /**
           * pendoAccount.id must be unique, but must match exactly the same in ADMIN so both apps can be tracked under the same venue.
           * In ADMIN this is in `src/app/common/services/tracking.service.ts` -> updatePendo.
           * All data must be exactly the same in ADMIN and DIARY because the most recent pendo.initialize will overwrite older records
           * of the same pendoAccount.id.
           */
          const envPrefix = window.config.pendo.envPrefix;
          const pendoAccount = {
            id: envPrefix + "_" + accountId + "_" + venue.id, // must be unique
            business_name: businessName,
            venue_id: venue.id,
            venue_name: venue.name,
            state: venue.state,
            country: venue.country,
            postcode: venue.postcode,
            timeZone: venue.timeZoneId,
            creation_date: createdAt,
            version: versionNumber // https://nowbookit.atlassian.net/browse/NBI-6592
          };

          const data = {
            visitor,
            account: pendoAccount
          };

          /**
           * If already initialized, call identify to continue same session (https://support.pendo.io/hc/en-us/articles/360031862272)
           * Even if you do a venue change, pendo.identify is better because calling `pendo.validateInstall()` in console
           * will return correct result this way, but not if you do pendo.initialize multiple times.
           */
          if (this.pendoInitialized) {
            window.pendo.identify(data);
            console.log(NS, "pendo.identify called with data.", data);
          } else {
            window.pendo.initialize(data);
            console.log(NS, "pendo.initialize called with creds.", data);
          }

          this.pendoInitialized = true;
        });
    }
  }

  // We are setting up pendo for anonymous user - Refer Jira ticket https://nowbookit.atlassian.net/browse/NBI-6587
  // Also Refer Pendo document - https://support.pendo.io/hc/en-us/articles/360032202751
  // Don't need `account: {id: 'ACCOUNT-UNIQUE-ID'}` as it is only the visitor property that gets uses for anonymous visitor session.
  runPendoAnonymously(): Promise<boolean> {
    if (!window.pendo) {
      console.log(NS, 'Note: Pendo is not configured for this environment.')
      return Promise.resolve(false);
    }
    if (this.pendoInitialized) {
      console.log(NS, 'prevented pendo from initializing anonymously a 2nd time.')
      return Promise.resolve(false);
    }

    
    const data = {visitor: {id: 'VISITOR-UNIQUE-ID'}};
    window.pendo.initialize(data);
    this.pendoInitialized = true;
    console.log(NS, "pendo.initialize called anonymously.", data);

    return this.checkForPendoGuide();
  }
  
  /**
   * Sometimes we need to show a Pendo guide to the user before they are directed to the login page.
   * This function checks for the presence of a Pendo guide and returns a promise that resolves to true if a guide is found.
   * If no guide is found, the promise resolves to false.
   */
  private checkForPendoGuide(): Promise<boolean> {
    
    return new Promise((resolve) => {
  
      let to: NodeJS.Timeout;
      let pollId: NodeJS.Timeout | undefined;
      const maxPollAttempts = 5;
      const pollIntervalMs = 500;
      const delayMs = 2000; // waits a bit to give Pendo message time to load
      let attempts = 0;
  
      const checkForGuide = () => {
        
        // At this point agent should have attached events API.
        const hasGuide = !!window.pendo.getActiveGuide();
        console.log(NS, 'pendo hasGuide', hasGuide);
        clearTimeout(to);
        if (pollId) clearInterval(pollId as unknown as number);
        resolve(hasGuide);
      };
  
      const tryBind = (): boolean => {
        if (window.pendo && typeof window.pendo.getActiveGuide === 'function') {
          checkForGuide();
          return true;
        }
        console.log(NS, 'pendo.getActiveGuide not found');
        return false;
      };

      setTimeout(() => {
        
        // Pendo agent loads asynchronously. Poll up to 5 times at 500ms intervals.
        if (!tryBind()) {
          pollId = setInterval(() => {
            attempts += 1;
            if (tryBind()) {
              if (pollId) clearInterval(pollId as unknown as number);
              return;
            }
            if (attempts >= maxPollAttempts) {
              if (pollId) clearInterval(pollId as unknown as number);
              // Could not bind within allotted attempts
              resolve(false);
            }
          }, pollIntervalMs) as unknown as NodeJS.Timeout;
          
          to = setTimeout(() => {
            if (pollId) clearInterval(pollId as unknown as number);
      
            // give up after waiting for ready/guidesLoaded
            resolve(false);
          }, pollIntervalMs * (maxPollAttempts + 1));
        }
    
      }, delayMs);
  
    });
  }

}

const instance = new TrackingService();
export default instance as TrackingService;
